import React from 'react';
import { Screen } from '../types';

const SuccessScreen = ({ userData, validity, startDate, endDate, navigateTo }) => {
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
  };

  // Generate permit number
  const generatePermitNo = () => {
    if (userData?.verificationId) {
      return `ILP-${userData.verificationId.slice(0, 8).toUpperCase()}`;
    }
    return `ILP-${Date.now().toString().slice(-8)}`;
  };

  // Get photo URL - handle different formats from DigiLocker
  const getPhotoUrl = () => {
    if (userData?.photoUrl) {
      if (userData.photoUrl.startsWith('data:')) {
        return userData.photoUrl;
      }
      if (userData.photoUrl.length > 100) {
        return `data:image/jpeg;base64,${userData.photoUrl}`;
      }
      return userData.photoUrl;
    }
    if (userData?.documentUrl) {
      return userData.documentUrl;
    }
    return null;
  };

  const photoUrl = getPhotoUrl();
  const hasPhoto = !!photoUrl;
  const isDigilockerVerified = !!userData?.verificationId;

  return (
    <div className="ilp-screen gray-bg">
      <div className="ilp-header white-bg">
        <h2>Your Official Permit</h2>
        <button className="ilp-icon-btn">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
        </button>
      </div>

      <div className="ilp-permit-content">
        <div className="ilp-permit-header">
          <div className="ilp-verified-identity-badge">
            <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 4.925-3.467 9.045-8.104 10.04a.75.75 0 01-.192 0C5.166 16.046 1.7 11.926 1.7 7.001c0-.68.056-1.35.166-2.002zm11.76 3.07a.75.75 0 00-1.06-1.06l-3.89 3.89-.935-.935a.75.75 0 00-1.06 1.06l1.465 1.465a.75.75 0 001.06 0l4.42-4.42z" clipRule="evenodd" />
            </svg>
            <span>{isDigilockerVerified ? 'DigiLocker Verified' : 'Verified Identity'}</span>
          </div>
          <h1>Permit Issued</h1>
          <p>Carry this digital permit during your stay.</p>
        </div>

        <div className="ilp-permit-card">
          <div className="ilp-permit-card-bar"></div>
          
          <div className="ilp-permit-profile">
            <div className="ilp-permit-photo">
              {hasPhoto ? (
                <img src={photoUrl} alt="Profile" />
              ) : (
                <div className="ilp-permit-photo-placeholder">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
              )}
              <div className="ilp-permit-verified-dot">
                <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="ilp-permit-info">
              <h3>{userData?.name || 'Applicant Name'}</h3>
              <p>Tourist ILP • {generatePermitNo()}</p>
              <div className="ilp-active-badge">
                <div className="ilp-pulse-dot"></div>
                <span>Active & Valid</span>
              </div>
            </div>
          </div>

          <div className="ilp-permit-divider">
            <div className="ilp-permit-notch left"></div>
            <div className="ilp-permit-dashed-line"></div>
            <div className="ilp-permit-notch right"></div>
          </div>

          <div className="ilp-permit-qr">
            <div className="ilp-qr-wrapper">
              <div className="ilp-qr-code">
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(JSON.stringify({
                    permit: generatePermitNo(),
                    name: userData?.name || 'Applicant',
                    aadhaar: userData?.aadhaar || 'XXXX-XXXX-XXXX',
                    validFrom: formatDate(startDate),
                    validTo: formatDate(endDate),
                    verified: isDigilockerVerified
                  }))}`} 
                  alt="QR Code" 
                />
              </div>
            </div>
            <p>Scan at Checkpoints ({isDigilockerVerified ? 'DigiLocker Verified' : 'Verified Identity'})</p>
          </div>

          <div className="ilp-permit-dates">
            <div className="ilp-permit-date">
              <label>Entry Date</label>
              <p>{formatDate(startDate)}</p>
            </div>
            <div className="ilp-permit-date">
              <label>Expiry Date</label>
              <p>{formatDate(endDate)}</p>
            </div>
          </div>
        </div>

        <div className="ilp-permit-actions">
          <button className="ilp-primary-btn full" onClick={() => navigateTo(Screen.ViewPermit)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span>Download Official ILP PDF</span>
          </button>
          
          <button className="ilp-dark-btn full">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.333 3H4.666C3.746 3 3 3.746 3 4.666v14.667c0 .921.746 1.667 1.666 1.667h14.667c.92 0 1.667-.746 1.667-1.667V4.666C21 3.746 20.253 3 19.333 3zm-3.048 11.458h-2.327c-.381 0-.69.309-.69.69s.309.691.69.691h2.327c.381 0 .69-.309.69-.691s-.309-.69-.69-.69zm0-2.324h-2.327c-.381 0-.69.309-.69.69s.309.69.69.69h2.327c.381 0 .69-.309.69-.69s-.309-.69-.69-.69z" />
            </svg>
            <span>Add to Apple Wallet</span>
          </button>

          <button className="ilp-link-btn" onClick={() => navigateTo(Screen.Home)}>
            Return to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuccessScreen;

